import { useCallback, useEffect, useRef, useState } from 'react'
import type { Category, Client, Entry } from '../../shared/types'
import { api } from '../api'
import { formatHours, parseDuration, minutesFromStartEnd } from '../../shared/duration'
import CategoryCombo from './CategoryCombo'
import { pushError, pushToast } from '../toast'
import { useAutosave } from '../useAutosave'
import { appToday, mmddyy } from '../datetime'
import { useColumns, ColGroup, ColResizer, ColumnsMenu, WrapText, type ColumnDef } from '../columns'

interface Row extends Entry {
  _key: number
}

const EDITABLE_FIELDS = ['entry_date', 'description', 'category_id', 'duration_minutes', 'start_time', 'end_time'] as const

export default function Grid({
  client,
  categories,
  initialEntries,
  defaultDate,
  onChanged,
}: {
  client: Client
  categories: Category[]
  initialEntries: Entry[]
  defaultDate: string
  onChanged: () => void
}) {
  const startend = client.duration_mode === 'startend'

  // Column model (visual order). 'select' + 'actions' are fixed control columns.
  const defs: ColumnDef[] = startend
    ? [
        { key: 'select', label: '', width: 36, fixed: true },
        { key: 'entry_date', label: 'Date', width: 130 },
        { key: 'description', label: 'Description', width: 320 },
        { key: 'category', label: 'Category', width: 190 },
        { key: 'start_time', label: 'Start', width: 90 },
        { key: 'end_time', label: 'End', width: 90 },
        { key: 'hours', label: 'Hours', width: 90 },
        { key: 'actions', label: '', width: 40, fixed: true },
      ]
    : [
        { key: 'select', label: '', width: 36, fixed: true },
        { key: 'entry_date', label: 'Date', width: 130 },
        { key: 'description', label: 'Description', width: 340 },
        { key: 'category', label: 'Category', width: 190 },
        { key: 'duration', label: 'Hours', width: 96 },
        { key: 'actions', label: '', width: 40, fixed: true },
      ]
  const colsApi = useColumns(`tracker-${startend ? 'se' : 'dec'}`, defs)

  const NAV_ALL = startend
    ? ['entry_date', 'description', 'category', 'start_time', 'end_time']
    : ['entry_date', 'description', 'category', 'duration']
  const navKeys = NAV_ALL.filter((k) => colsApi.isVisible(k))

  const keyCounter = useRef(1)
  const nextKey = () => keyCounter.current++

  // New rows always start on the default date (today, when the visible month is
  // the current one) rather than inheriting the date of the row above. Ctrl+D
  // still copies the date down when a run of entries shares a day.
  const makeBlank = useCallback(
    (): Row => ({
      _key: nextKey(),
      id: 0,
      client_id: client.id,
      entry_date: defaultDate,
      description: '',
      category_id: null,
      duration_minutes: 0,
      start_time: null,
      end_time: null,
      invoice_id: null,
      created_at: '',
      updated_at: '',
    }),
    [client.id, defaultDate],
  )

  const [rows, setRows] = useState<Row[]>(() => {
    const rs: Row[] = initialEntries.map((e) => ({ ...e, _key: nextKey() }))
    rs.push(makeBlank())
    return rs
  })
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const saved = useRef<Map<number, Partial<Entry>>>(new Map())
  useEffect(() => {
    for (const e of initialEntries) saved.current.set(e.id, { ...e })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { scheduleSave, flushAll, savingCount, hasError } = useAutosave({
    onSaved: (id, patch) => {
      const prev = saved.current.get(id) ?? {}
      saved.current.set(id, { ...prev, ...patch })
    },
    onChanged,
  })

  const creating = useRef<Set<number>>(new Set())
  const setLocal = useCallback((key: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)))
  }, [])

  const createRow = useCallback(
    async (key: number) => {
      if (creating.current.has(key)) return
      creating.current.add(key)
      const row = rowsRef.current.find((r) => r._key === key)
      if (!row) return
      const body = {
        client_id: client.id,
        entry_date: row.entry_date || defaultDate || appToday(),
        description: row.description,
        category_id: row.category_id,
        duration_minutes: row.duration_minutes,
        start_time: row.start_time,
        end_time: row.end_time,
      }
      try {
        const created = await api.createEntry(body)
        saved.current.set(created.id, { ...created })
        setRows((rs) => {
          const mapped = rs.map((r) => (r._key === key ? { ...r, id: created.id } : r))
          if (!mapped.some((r) => r.id === 0)) mapped.push(makeBlank())
          return mapped
        })
        const latest = rowsRef.current.find((r) => r._key === key)
        if (latest) {
          const diff: Partial<Entry> = {}
          for (const f of EDITABLE_FIELDS) if ((latest as any)[f] !== (created as any)[f]) (diff as any)[f] = (latest as any)[f]
          if (Object.keys(diff).length) scheduleSave(created.id, diff)
        }
        onChanged()
      } catch (e) {
        pushError(`Could not save new entry: ${(e as Error).message}`)
      } finally {
        creating.current.delete(key)
      }
    },
    [client.id, defaultDate, makeBlank, onChanged, scheduleSave],
  )

  const commit = useCallback(
    (row: Row, rawPatch: Partial<Row>) => {
      if (row.invoice_id != null) return
      let patch = rawPatch
      if (startend && ('start_time' in patch || 'end_time' in patch)) {
        const merged = { ...(rowsRef.current.find((r) => r._key === row._key) ?? row), ...patch }
        patch = { ...patch, duration_minutes: minutesFromStartEnd(merged.start_time, merged.end_time) }
      }
      setLocal(row._key, patch)
      const current = rowsRef.current.find((r) => r._key === row._key)
      const id = current?.id ?? row.id
      if (id === 0) void createRow(row._key)
      else {
        const savePatch: Partial<Entry> = {}
        for (const f of EDITABLE_FIELDS) if (f in patch) (savePatch as any)[f] = (patch as any)[f]
        if (Object.keys(savePatch).length) scheduleSave(id, savePatch)
      }
    },
    [createRow, scheduleSave, setLocal, startend],
  )

  // ── navigation (key-based so hidden columns are skipped) ──
  const refs = useRef<Map<string, HTMLInputElement | HTMLTextAreaElement>>(new Map())
  const refKey = (rowKey: number, colKey: string) => `${rowKey}|${colKey}`
  const register = (rowKey: number, colKey: string) => (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    const k = refKey(rowKey, colKey)
    if (el) refs.current.set(k, el)
    else refs.current.delete(k)
  }
  const focusKey = useCallback((rowIdx: number, colKey: string, caret?: 'start' | 'end') => {
    const rs = rowsRef.current
    if (rowIdx < 0 || rowIdx >= rs.length || !colKey) return
    const el = refs.current.get(refKey(rs[rowIdx]._key, colKey))
    if (!el) return
    el.focus()
    try {
      if (caret === 'end') el.setSelectionRange(el.value.length, el.value.length)
      else if (caret === 'start') el.setSelectionRange(0, 0)
    } catch {
      /* ignore */
    }
  }, [])

  const navKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIdx: number, colKey: string) => {
      const el = e.currentTarget as HTMLInputElement
      const mod = e.ctrlKey || e.metaKey
      const idx = navKeys.indexOf(colKey)
      if (mod && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        fillDown(rowIdx, colKey)
        return
      }
      if (mod && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault()
        deleteRow(rowIdx)
        return
      }
      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          flushAll()
          focusKey(e.shiftKey ? rowIdx - 1 : rowIdx + 1, colKey, 'end')
          break
        case 'Tab':
          e.preventDefault()
          if (e.shiftKey) {
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
            else focusKey(rowIdx - 1, navKeys[navKeys.length - 1], 'end')
          } else {
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'end')
            else focusKey(rowIdx + 1, navKeys[0], 'end')
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          focusKey(rowIdx + 1, colKey, 'end')
          break
        case 'ArrowUp':
          e.preventDefault()
          focusKey(rowIdx - 1, colKey, 'end')
          break
        case 'ArrowLeft':
          if (el.selectionStart === 0 && el.selectionEnd === 0) {
            e.preventDefault()
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
          }
          break
        case 'ArrowRight':
          if (el.selectionStart === el.value.length && el.selectionEnd === el.value.length) {
            e.preventDefault()
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'start')
          }
          break
        case 'Escape': {
          e.preventDefault()
          const row = rowsRef.current[rowIdx]
          if (row && row.id !== 0) {
            const sv = saved.current.get(row.id)
            if (sv) revertCell(row, colKey, sv)
          }
          break
        }
      }
    },
    [navKeys, focusKey, flushAll],
  )

  function revertCell(row: Row, colKey: string, sv: Partial<Entry>) {
    if (colKey === 'category') setLocal(row._key, { category_id: sv.category_id ?? null })
    else if (colKey === 'duration') setLocal(row._key, { duration_minutes: sv.duration_minutes ?? 0 })
    else setLocal(row._key, { [colKey]: (sv as any)[colKey] ?? '' } as Partial<Row>)
  }

  function fillDown(rowIdx: number, colKey: string) {
    const above = rowsRef.current[rowIdx - 1]
    const row = rowsRef.current[rowIdx]
    if (!above || !row || row.invoice_id != null) return
    if (colKey === 'category') commit(row, { category_id: above.category_id })
    else if (colKey === 'duration') commit(row, { duration_minutes: above.duration_minutes })
    else commit(row, { [colKey]: (above as any)[colKey] } as Partial<Row>)
  }

  async function deleteRow(rowIdx: number) {
    const row = rowsRef.current[rowIdx]
    if (!row || row.id === 0) return
    if (row.invoice_id != null) {
      pushError('This entry is on an issued invoice and cannot be deleted.')
      return
    }
    const snapshot = { ...row }
    setRows((rs) => rs.filter((r) => r._key !== row._key))
    setSelected((s) => {
      const n = new Set(s)
      n.delete(row._key)
      return n
    })
    try {
      await api.deleteEntry(row.id)
      onChanged()
      pushToast('Entry deleted', { actionLabel: 'Undo', onAction: () => restoreRows([snapshot], rowIdx) })
    } catch (e) {
      setRows((rs) => {
        const copy = [...rs]
        copy.splice(rowIdx, 0, snapshot)
        return copy
      })
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  async function restoreRows(snaps: Row[], insertAt: number) {
    try {
      const restored = await Promise.all(
        snaps.map((s) =>
          api.createEntry({
            client_id: s.client_id,
            entry_date: s.entry_date,
            description: s.description,
            category_id: s.category_id,
            duration_minutes: s.duration_minutes,
            start_time: s.start_time,
            end_time: s.end_time,
          }),
        ),
      )
      setRows((rs) => {
        const copy = [...rs]
        const at = Math.min(Math.max(insertAt, 0), copy.length - 1)
        copy.splice(at, 0, ...restored.map((r) => ({ ...r, _key: nextKey() })))
        return copy
      })
      for (const r of restored) saved.current.set(r.id, { ...r })
      onChanged()
    } catch (e) {
      pushError(`Undo failed: ${(e as Error).message}`)
    }
  }

  // selection
  const selectableKeys = rows.filter((r) => r.id !== 0 && r.invoice_id == null).map((r) => r._key)
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k))
  function toggleRow(key: number) {
    setSelected((s) => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  }
  function toggleAll() {
    setSelected(() => (allSelected ? new Set() : new Set(selectableKeys)))
  }
  async function bulkDelete() {
    const victims = rows.filter((r) => selected.has(r._key) && r.id !== 0 && r.invoice_id == null)
    if (victims.length === 0) return
    const snaps = victims.map((v) => ({ ...v }))
    const firstIdx = rows.findIndex((r) => r._key === victims[0]._key)
    const keys = new Set(victims.map((v) => v._key))
    setRows((rs) => rs.filter((r) => !keys.has(r._key)))
    setSelected(new Set())
    const results = await Promise.allSettled(victims.map((v) => api.deleteEntry(v.id)))
    const failed = results.filter((r) => r.status === 'rejected').length
    onChanged()
    if (failed > 0) pushError(`${failed} of ${victims.length} could not be deleted.`)
    pushToast(`${victims.length - failed} entr${victims.length - failed === 1 ? 'y' : 'ies'} deleted`, {
      actionLabel: 'Undo',
      onAction: () => restoreRows(snaps, Math.max(firstIdx, 0)),
    })
  }

  // ── per-cell renderer ──
  function renderCell(colKey: string, row: Row, rowIdx: number) {
    const readOnly = row.invoice_id != null
    const isBlank = row.id === 0
    if (colKey === 'select') {
      return (
        <td key="select" className="px-2 py-1 text-center">
          {!isBlank && !readOnly && (
            <input type="checkbox" tabIndex={-1} checked={selected.has(row._key)} onChange={() => toggleRow(row._key)} aria-label="Select row" />
          )}
        </td>
      )
    }
    if (colKey === 'actions') {
      return (
        <td key="actions" className="px-1 py-1 text-center">
          {!isBlank && !readOnly && (
            <button tabIndex={-1} className="text-slate-300 hover:text-red-500" title="Delete row" aria-label="Delete row" onClick={() => deleteRow(rowIdx)}>
              ✕
            </button>
          )}
        </td>
      )
    }
    if (colKey === 'hours') {
      return (
        <td key="hours" className="grid-cell px-2 py-1 text-right tabular text-slate-500">
          {formatHours(row.start_time && row.end_time ? minutesFromStartEnd(row.start_time, row.end_time) : row.duration_minutes)}
        </td>
      )
    }
    const onKey = (e: React.KeyboardEvent) => navKeyDown(e, rowIdx, colKey)
    if (colKey === 'category') {
      const cats = categories.slice()
      if (row.category_id != null && !cats.some((c) => c.id === row.category_id)) {
        cats.push({ id: row.category_id, client_id: row.client_id, name: '(inactive)', rate_cents: null, sort_order: 999, active: 0 })
      }
      return (
        <td key="category" className="border-r border-slate-100 align-middle">
          <CategoryCombo
            value={row.category_id}
            categories={cats}
            disabled={readOnly}
            onCommit={(id) => commit(row, { category_id: id })}
            gridKeyDown={onKey}
            registerRef={register(row._key, 'category')}
          />
        </td>
      )
    }
    if (colKey === 'duration') {
      return (
        <td key="duration" className="border-r border-slate-100 align-middle">
          <DurationCell minutes={row.duration_minutes} readOnly={readOnly} onCommit={(m) => commit(row, { duration_minutes: m })} onKeyDown={onKey} registerRef={register(row._key, 'duration')} />
        </td>
      )
    }
    // text cells: entry_date, description, start_time, end_time
    const value = ((row as any)[colKey] ?? '') as string
    const isTime = colKey === 'start_time' || colKey === 'end_time'
    if (colKey === 'description') {
      return (
        <td key="description" className="border-r border-slate-100 align-middle">
          <WrapText wrap={colsApi.wrap} value={value} disabled={readOnly} onChange={(v) => commit(row, { description: v })} onKeyDown={onKey} registerRef={register(row._key, 'description')} />
        </td>
      )
    }
    if (colKey === 'entry_date') {
      return (
        <td key="entry_date" className="border-r border-slate-100 align-middle">
          <DateCell value={value} readOnly={readOnly} onChange={(v) => commit(row, { entry_date: v })} onKeyDown={onKey} registerRef={register(row._key, 'entry_date')} />
        </td>
      )
    }
    return (
      <td key={colKey} className="border-r border-slate-100 align-middle">
        <input
          ref={register(row._key, colKey)}
          className="grid-cell w-full bg-transparent px-2 py-1 tabular outline-none disabled:text-slate-400"
          value={value}
          disabled={readOnly}
          placeholder={colKey === 'entry_date' ? 'YYYY-MM-DD' : isTime ? 'HH:MM' : ''}
          onChange={(e) => commit(row, { [colKey]: e.target.value } as Partial<Row>)}
          onKeyDown={onKey}
        />
      </td>
    )
  }

  return (
    <div>
      <div className="mb-1 flex h-6 items-center justify-between text-xs">
        {selected.size > 0 ? (
          <div className="flex items-center gap-3">
            <span className="font-medium text-slate-700">{selected.size} selected</span>
            <button className="font-medium text-red-600 hover:underline" onClick={bulkDelete}>
              Delete selected
            </button>
            <button className="text-slate-500 hover:underline" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <SaveIndicator saving={savingCount > 0} error={hasError} />
          <ColumnsMenu cols={colsApi} />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="border-collapse" style={{ tableLayout: 'fixed', width: colsApi.totalWidth }}>
          <ColGroup cols={colsApi} />
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              {colsApi.visible.map((d) =>
                d.key === 'select' ? (
                  <th key="select" className="px-2 py-2 text-center">
                    <input type="checkbox" tabIndex={-1} checked={allSelected} onChange={toggleAll} aria-label="Select all" disabled={selectableKeys.length === 0} />
                  </th>
                ) : d.key === 'actions' ? (
                  <th key="actions" className="px-2 py-2" />
                ) : (
                  <th key={d.key} className="relative px-2 py-2">
                    {d.label}
                    <ColResizer cols={colsApi} colKey={d.key} />
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const readOnly = row.invoice_id != null
              return (
                <tr
                  key={row._key}
                  className={
                    'border-b border-slate-100 ' +
                    (readOnly ? 'bg-slate-50/60' : selected.has(row._key) ? 'bg-blue-50/50' : 'bg-white hover:bg-slate-50/40')
                  }
                  title={readOnly ? 'On an issued invoice — read-only' : undefined}
                >
                  {colsApi.visible.map((d) => renderCell(d.key, row, rowIdx))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Arrows / Tab / Enter to move · type in the last row to add · {navHint()}+D fill down · {navHint()}+⌫ delete · Esc revert · drag column edges to resize
      </p>
    </div>
  )
}

function navHint() {
  return typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘' : 'Ctrl'
}

function SaveIndicator({ saving, error }: { saving: boolean; error: boolean }) {
  if (error) return <span className="font-medium text-red-600">⚠ Unsaved changes</span>
  if (saving) return <span className="text-slate-400">Saving…</span>
  return <span className="text-slate-300">Saved</span>
}

// Displays MM/DD/YY when not focused; shows the raw ISO value while editing.
function DateCell({
  value,
  readOnly,
  onChange,
  onKeyDown,
  registerRef,
}: {
  value: string
  readOnly: boolean
  onChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}) {
  const [editing, setEditing] = useState(false)
  return (
    <input
      ref={registerRef}
      className="grid-cell w-full bg-transparent px-2 py-1 tabular outline-none disabled:text-slate-400"
      value={editing ? value : value ? mmddyy(value) : ''}
      disabled={readOnly}
      placeholder="YYYY-MM-DD"
      onFocus={() => setEditing(true)}
      onBlur={() => setEditing(false)}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
  )
}

function DurationCell({
  minutes,
  readOnly,
  onCommit,
  onKeyDown,
  registerRef,
}: {
  minutes: number
  readOnly: boolean
  onCommit: (minutes: number) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}) {
  const [text, setText] = useState(() => (minutes ? formatHours(minutes) : ''))
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!editing) setText(minutes ? formatHours(minutes) : '')
  }, [minutes, editing])
  function done() {
    setEditing(false)
    const parsed = parseDuration(text)
    if (parsed == null) {
      setText(minutes ? formatHours(minutes) : '')
      return
    }
    if (parsed !== minutes) onCommit(parsed)
    else setText(parsed ? formatHours(parsed) : '')
  }
  return (
    <input
      ref={registerRef}
      className="grid-cell w-full bg-transparent px-2 py-1 text-right tabular outline-none disabled:text-slate-400"
      value={text}
      disabled={readOnly}
      placeholder="0"
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        if (e.key === 'Enter') done()
        onKeyDown(e)
      }}
    />
  )
}
